// AWS Cognito authentication service
import { 
  cognitoConfig, 
  getCognitoUrls,
  buildAuthorizationUrl,
  buildLogoutUrl,
  validateCognitoConfig
} from '@/config/cognito'
import type { TokenExchangePayload, TokenResponse, CognitoUser } from '@/config/cognito'
import { captureSentryException, addSentryBreadcrumb } from '@/config/sentry'

export class AuthService {
  private static instance: AuthService
  private loginInProgress = false
  
  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService()
    }
    return AuthService.instance
  }

  // Redirect to Cognito Hosted Pages for authentication
  public redirectToLogin(registry: string = 'sor'): void {
    try {
      // Prevent multiple rapid login attempts
      if (this.loginInProgress) {
        addSentryBreadcrumb('Login already in progress - ignoring duplicate request', 'auth', 'warning')
        return
      }
      
      this.loginInProgress = true

      if (!validateCognitoConfig()) {
        this.loginInProgress = false
        throw new Error('Cognito configuration is incomplete')
      }

      addSentryBreadcrumb(`Redirecting to login for registry: ${registry}`, 'navigation')
      
      const authUrl = buildAuthorizationUrl(registry)
      
      // Reset flag after a delay to allow for page navigation
      setTimeout(() => {
        this.loginInProgress = false
      }, 5000)
      
      window.location.href = authUrl
    } catch (error) {
      captureSentryException(error as Error, {
        service: 'AuthService',
        action: 'redirectToLogin',
        registry
      })
      throw error
    }
  }

  // Exchange authorization code for tokens
  public async exchangeCodeForTokens(code: string, state: string): Promise<TokenResponse> {
    try {
      addSentryBreadcrumb('Exchanging authorization code for tokens', 'auth')

      // Retrieve state from session storage, fallback to localStorage
      let storedState = sessionStorage.getItem('auth_state')
      let authDataSource = 'sessionStorage'
      
      if (!storedState) {
        // Try localStorage fallback
        const tempAuthData = localStorage.getItem('auth_temp')
        if (tempAuthData) {
          try {
            const authData = JSON.parse(tempAuthData)
            // Check if auth data is recent (within 10 minutes)
            const isRecent = Date.now() - authData.timestamp < 10 * 60 * 1000
            if (isRecent) {
              storedState = authData.state
              authDataSource = 'localStorage'
            }
          } catch (e) {
            // Invalid JSON, ignore
          }
        }
      }

      if (!storedState) {
        addSentryBreadcrumb('No stored state found - possible session cleared', 'auth', 'warning')
        throw new Error('No authentication state found - please try logging in again')
      }

      if (state !== storedState) {
        addSentryBreadcrumb(`State mismatch: received=${state}, stored=${storedState} (from ${authDataSource})`, 'auth', 'error')
        throw new Error('State parameter mismatch - possible CSRF attack')
      }

      addSentryBreadcrumb(`State verified successfully from ${authDataSource}`, 'auth')

      const payload: TokenExchangePayload = {
        grant_type: 'authorization_code',
        client_id: cognitoConfig.clientId,
        code,
        redirect_uri: cognitoConfig.redirectUri
      }

      const response = await fetch(getCognitoUrls().token, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(payload as any)
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Token exchange failed: ${response.status} ${errorText}`)
      }

      const tokens: TokenResponse = await response.json()

      // Clean up session storage and temporary auth data
      sessionStorage.removeItem('auth_state')
      sessionStorage.removeItem('auth_registry')
      localStorage.removeItem('auth_temp')

      addSentryBreadcrumb('Token exchange successful', 'auth')
      return tokens
    } catch (error) {
      captureSentryException(error as Error, {
        service: 'AuthService',
        action: 'exchangeCodeForTokens'
      })
      throw error
    }
  }

  // Get user information using access token
  public async getUserInfo(accessToken: string): Promise<CognitoUser> {
    try {
      addSentryBreadcrumb('Fetching user info', 'auth')

      const response = await fetch(getCognitoUrls().userInfo, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch user info: ${response.status}`)
      }

      const userInfo: CognitoUser = await response.json()
      
      addSentryBreadcrumb(`User info retrieved for: ${userInfo.email}`, 'auth')
      return userInfo
    } catch (error) {
      captureSentryException(error as Error, {
        service: 'AuthService',
        action: 'getUserInfo'
      })
      throw error
    }
  }

  // Refresh access token using refresh token
  public async refreshTokens(refreshToken: string): Promise<TokenResponse> {
    try {
      addSentryBreadcrumb('Refreshing access token', 'auth')

      const payload = {
        grant_type: 'refresh_token',
        client_id: cognitoConfig.clientId,
        refresh_token: refreshToken
      }

      const response = await fetch(getCognitoUrls().token, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(payload)
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Token refresh failed: ${response.status} ${errorText}`)
      }

      const tokens: TokenResponse = await response.json()
      
      addSentryBreadcrumb('Token refresh successful', 'auth')
      return tokens
    } catch (error) {
      captureSentryException(error as Error, {
        service: 'AuthService',
        action: 'refreshTokens'
      })
      throw error
    }
  }

  // Logout and redirect to Cognito logout page
  public logout(): void {
    try {
      addSentryBreadcrumb('User logging out', 'auth')

      // Clear any local storage/session storage
      this.clearLocalAuth()

      // Redirect to Cognito logout
      const logoutUrl = buildLogoutUrl()
      window.location.href = logoutUrl
    } catch (error) {
      captureSentryException(error as Error, {
        service: 'AuthService',
        action: 'logout'
      })
      throw error
    }
  }

  // Clear local authentication data
  public clearLocalAuth(): void {
    try {
      // Clear session storage
      sessionStorage.removeItem('auth_state')
      sessionStorage.removeItem('auth_registry')

      // Clear any stored tokens and temporary auth data
      localStorage.removeItem('auth_tokens')
      localStorage.removeItem('user_info')
      localStorage.removeItem('auth_temp')

      addSentryBreadcrumb('Local auth data cleared', 'auth')
    } catch (error) {
      captureSentryException(error as Error, {
        service: 'AuthService',
        action: 'clearLocalAuth'
      })
    }
  }

  // Decode JWT token (basic decode without verification)
  public decodeJWT(token: string): any {
    try {
      const parts = token.split('.')
      if (parts.length !== 3) {
        throw new Error('Invalid JWT format')
      }
      
      const base64Url = parts[1]!  // Safe to use ! since we checked length above
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
      return JSON.parse(jsonPayload)
    } catch (error) {
      captureSentryException(error as Error, {
        service: 'AuthService',
        action: 'decodeJWT'
      })
      throw new Error('Invalid JWT token')
    }
  }

  // Check if token is expired
  public isTokenExpired(token: string): boolean {
    try {
      const decoded = this.decodeJWT(token)
      const currentTime = Math.floor(Date.now() / 1000)
      return decoded.exp < currentTime
    } catch (error) {
      return true // Assume expired if we can't decode
    }
  }

  // Validate authentication state on app initialization
  public async validateAuthState(): Promise<{ isAuthenticated: boolean; user?: CognitoUser }> {
    try {
      const storedTokens = localStorage.getItem('auth_tokens')
      
      if (!storedTokens) {
        return { isAuthenticated: false }
      }

      const tokens: TokenResponse = JSON.parse(storedTokens)

      // Check if access token is expired
      if (this.isTokenExpired(tokens.access_token)) {
        // Try to refresh token
        try {
          const newTokens = await this.refreshTokens(tokens.refresh_token)
          localStorage.setItem('auth_tokens', JSON.stringify(newTokens))
          
          const user = await this.getUserInfo(newTokens.access_token)
          return { isAuthenticated: true, user }
        } catch (refreshError) {
          // Refresh failed, clear auth state
          this.clearLocalAuth()
          return { isAuthenticated: false }
        }
      }

      // Token is valid, get user info
      const user = await this.getUserInfo(tokens.access_token)
      return { isAuthenticated: true, user }

    } catch (error) {
      captureSentryException(error as Error, {
        service: 'AuthService',
        action: 'validateAuthState'
      })
      this.clearLocalAuth()
      return { isAuthenticated: false }
    }
  }
}

// Export singleton instance
export const authService = AuthService.getInstance()